import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/apiClient';
import '../styles/BlockedAccountScreen.css';

// Get polling interval from env (default: 30 seconds)
const POLLING_INTERVAL = Number(import.meta.env.VITE_INACTIVE_CHECK_INTERVAL || '30') * 1000;

/**
 * BlockedAccountScreen - Displayed when user account is inactive
 *
 * Shows a message explaining that the account is inactive and provides
 * a logout button to allow the user to sign in with a different account.
 *
 * Features:
 * - Polls GET /me every INACTIVE_CHECK_INTERVAL seconds to detect reactivation
 * - Automatically returns to protected shell when account becomes active
 */
export const BlockedAccountScreen: React.FC = () => {
  const navigate = useNavigate();
  const { logout, isLoggingIn, refreshUser } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [pollingError, setPollingError] = React.useState<string | null>(null);
  const [logoutError, setLogoutError] = React.useState<string | null>(null);
  const pollingRef = React.useRef(false);

  const handleChangeAccount = async () => {
    setIsLoggingOut(true);
    setLogoutError(null);
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      setLogoutError('Erreur lors de la déconnexion. Veuillez réessayer.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Polling effect: check /me periodically to detect account reactivation
  React.useEffect(() => {
    console.log('[BlockedAccountScreen] Starting polling every', POLLING_INTERVAL / 1000, 'seconds');

    // Poll function
    const poll = async () => {
      if (pollingRef.current) {
        // Already polling, skip this tick
        return;
      }

      pollingRef.current = true;
      setPollingError(null); // Clear previous errors

      try {
        console.log('[BlockedAccountScreen] Polling /me...');
        const response = await api.getMe();

        if (response.success && response.data) {
          const user = response.data;
          console.log('[BlockedAccountScreen] /me response:', {
            email: user.email,
            is_active: user.is_active
          });

          if (user.is_active === true) {
            console.log('[BlockedAccountScreen] Account is now active! Refreshing user profile...');

            // Refresh user in auth context
            if (refreshUser) {
              await refreshUser();
              console.log('[BlockedAccountScreen] User profile refreshed, returning to shell');
            }
          } else {
            console.log('[BlockedAccountScreen] Account still inactive, will retry in', POLLING_INTERVAL / 1000, 'seconds');
            setPollingError(null); // Clear error on successful poll
          }
        } else {
          // Handle error responses
          if (response.code === 'USER_INACTIVE') {
            console.log('[BlockedAccountScreen] Account still inactive (403 USER_INACTIVE)');
            setPollingError(null); // Not really an error, expected state
          } else if (response.code === 'UNAUTHORIZED') {
            console.warn('[BlockedAccountScreen] Token expired (401), need reauth');
            setPollingError('Votre session a expiré. Veuillez vous reconnecter.');
          } else if (response.code === 'NETWORK_ERROR') {
            console.error('[BlockedAccountScreen] Network error:', response.error);
            setPollingError('Erreur de connexion au serveur. Vérification en cours...');
          } else {
            console.error('[BlockedAccountScreen] Failed to poll /me:', response.error);
            setPollingError('Une erreur est survenue lors de la vérification de votre compte.');
          }
        }
      } catch (err) {
        console.error('[BlockedAccountScreen] Polling error:', err);
        setPollingError('Erreur de connexion au serveur. Nouvelle tentative dans quelques secondes...');
      } finally {
        pollingRef.current = false;
      }
    };

    // Start polling immediately
    poll();

    // Set up interval
    const intervalId = setInterval(poll, POLLING_INTERVAL);

    // Cleanup on unmount
    return () => {
      console.log('[BlockedAccountScreen] Stopping polling');
      clearInterval(intervalId);
    };
  }, []); // Empty deps: only run on mount/unmount

  return (
    <div className="blocked-account-screen">
      <div className="blocked-account-box">
        {/* Icon */}
        <div className="blocked-account-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>

        {/* Title */}
        <h1 className="blocked-account-title" tabIndex={-1}>
          Compte non autorisé
        </h1>

        {/* Message */}
        <p className="blocked-account-message">
          Votre compte n'est pas autorisé à accéder à Corpus.
        </p>
        <p className="blocked-account-message">
          Contactez votre administrateur pour activer votre accès, ou connectez-vous avec un autre compte.
        </p>

        {/* Status indicator */}
        {pollingError ? (
          <p className="blocked-account-status" style={{ color: '#ef4444', opacity: 1 }}>
            {pollingError}
          </p>
        ) : (
          <p className="blocked-account-status">
            Vérification automatique en cours...
          </p>
        )}

        {/* Logout error */}
        {logoutError && (
          <p className="blocked-account-status" style={{ color: '#ef4444', opacity: 1 }}>
            {logoutError}
          </p>
        )}

        {/* Actions */}
        <div className="blocked-account-actions">
          <button
            className="blocked-account-button blocked-account-button-primary"
            onClick={handleChangeAccount}
            disabled={isLoggingOut || isLoggingIn}
            aria-label="Changer de compte"
          >
            {isLoggingOut ? 'Déconnexion...' : 'Changer de compte'}
          </button>
        </div>
      </div>
    </div>
  );
};
